;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Constants
  // ============================================================================

  /**
   * Fixed distance threshold for clustering in meters.
   * Only markers within this physical distance will be clustered together.
   */
  const CLUSTER_DISTANCE_METERS = 60
  const METERS_PER_DEGREE_LAT = 111320

  // ============================================================================
  // Distance Calculation
  // ============================================================================

  const toRadians = (value) => (value * Math.PI) / 180

  function haversineDistanceMeters(pos1, pos2) {
    const R = 6371000
    const dLat = toRadians(pos2.lat - pos1.lat)
    const dLng = toRadians(pos2.lng - pos1.lng)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(pos1.lat)) *
        Math.cos(toRadians(pos2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  /**
   * Calculate distance between two lat/lng points in meters using Haversine formula.
   * Uses the shared implementation from geometry module if available.
   * @param {Object} pos1 - { lat, lng }
   * @param {Object} pos2 - { lat, lng }
   * @returns {number} Distance in meters
   */
  function calculateDistance(pos1, pos2) {
    const { geometry = {} } = namespace
    if (geometry.computeDistanceMeters) {
      return geometry.computeDistanceMeters(pos1.lat, pos1.lng, pos2.lat, pos2.lng)
    }
    return haversineDistanceMeters(pos1, pos2)
  }

  // ============================================================================
  // Cluster Renderer
  // ============================================================================

  /**
   * Custom renderer for cluster markers.
   * Uses category-based icons with the highest priority category from clustered markers.
   * Z-index is based on category priority so clusters appear above regular pins of the same category.
   */
  class CustomClusterRenderer {
    constructor(onClusterClick) {
      this.onClusterClick = onClusterClick
    }

    /**
     * Get the category priority map from the map module.
     * Falls back to default values if not available.
     */
    getCategoryPriority() {
      const { map: mapModule = {} } = namespace
      return mapModule.CATEGORY_PRIORITY || {
        certified_salon: 40,
        elite: 30,
        masters: 20,
        certified_stylist: 10,
        default: 5,
      }
    }

    /**
     * Get the highest priority category from a list of markers.
     * @param {Array} markers - Array of markers
     * @returns {string} Highest priority category
     */
    getHighestPriorityCategory(markers) {
      const categoryPriority = this.getCategoryPriority()
      let highestPriority = 0
      let highestCategory = 'default'

      for (const marker of markers) {
        const category = marker.category
        if (category && categoryPriority[category]) {
          const priority = categoryPriority[category]
          if (priority > highestPriority) {
            highestPriority = priority
            highestCategory = category
          }
        }
      }

      return highestCategory
    }

    render(cluster, _stats, map) {
      if (!globalThis.google || !globalThis.google.maps) return null

      const { templates = {}, map: mapModule = {} } = namespace
      const { buildClusterIcon = () => null } = templates
      const { getClusterZIndex } = mapModule

      const google = globalThis.google
      const count = cluster.count
      const position = cluster.position
      const highestPriorityCategory = this.getHighestPriorityCategory(cluster.markers || [])
      const icon = buildClusterIcon(count, highestPriorityCategory)

      if (!icon) return null

      // Use category-based z-index so clusters appear above regular pins of the same category
      const zIndex = getClusterZIndex
        ? getClusterZIndex(highestPriorityCategory)
        : this.getCategoryPriority()[highestPriorityCategory] + 5

      const marker = new google.maps.Marker({
        position,
        icon,
        zIndex,
        map,
        clickable: true,
      })

      if (this.onClusterClick) {
        marker.addListener('click', () => {
          this.onClusterClick(cluster)
        })
      }

      return marker
    }
  }

  // ============================================================================
  // Star Mesh (Cluster Expansion)
  // ============================================================================

  /**
   * Normalize a position to a Google Maps LatLng object.
   * @param {google.maps.LatLng|Object} position - Position to normalize
   * @returns {google.maps.LatLng|null} Normalized LatLng or null if invalid
   */
  function normalizeLatLng(position) {
    const google = globalThis.google
    if (!google || !google.maps) return null

    if (position instanceof google.maps.LatLng) {
      return position
    }
    if (position && typeof position.lat === 'function') {
      return new google.maps.LatLng(position.lat(), position.lng())
    }
    if (position && typeof position.lat === 'number') {
      return new google.maps.LatLng(position.lat, position.lng)
    }
    return null
  }

  /**
   * Calculate star mesh positions in a radial pattern using fixed pixel distance.
   * @param {google.maps.Map} map - Map instance
   * @param {google.maps.LatLng} centerLatLng - Normalized center position
   * @param {number} count - Number of positions needed
   * @param {number} radiusPixels - Radius in pixels
   * @returns {Array<google.maps.LatLng>} Array of positions
   */
  function calculateStarMeshPositions(map, centerLatLng, count, radiusPixels = 120) {
    if (!globalThis.google || !map || count === 0) return []

    const google = globalThis.google
    const zoom = map.getZoom() || 10
    const centerLat = centerLatLng.lat()
    const centerLng = centerLatLng.lng()
    const latRadians = (centerLat * Math.PI) / 180

    // Convert pixel distance to meters based on zoom level
    const metersPerPixel = (156543.03392 * Math.cos(latRadians)) / Math.pow(2, zoom)
    const radiusMeters = radiusPixels * metersPerPixel

    const positions = []
    const angleStep = (2 * Math.PI) / count

    for (let i = 0; i < count; i++) {
      let angle = i * angleStep

      // For clusters with exactly 2 items, use diagonal positioning instead of horizontal
      if (count === 2) {
        angle = (i === 0) ? Math.PI / 4 : (5 * Math.PI) / 4  // 45° and 225° (diagonal)
      }

      const latOffsetDegrees = (radiusMeters / METERS_PER_DEGREE_LAT) * Math.cos(angle)
      const lngOffsetDegrees = (radiusMeters / (METERS_PER_DEGREE_LAT * Math.cos(latRadians))) * Math.sin(angle)

      positions.push(new google.maps.LatLng(centerLat + latOffsetDegrees, centerLng + lngOffsetDegrees))
    }

    return positions
  }

  /**
   * Calculate dynamic radius based on number of items.
   * @param {number} count - Number of items
   * @returns {number} Radius in pixels
   */
  function calculateMeshRadius(count) {
    let radius
    if (count <= 2) {
      radius = 15
    } else if (count <= 5) {
      radius = 20 + (count - 2) * 3
    } else if (count <= 10) {
      radius = 29 + (count - 5) * 4
    } else {
      radius = 60 + Math.sqrt(count - 10) * 8
    }
    return Math.min(radius, 80)
  }

  function isValidStylistLocation(stylist) {
    return stylist && Number.isFinite(stylist.latitude) && Number.isFinite(stylist.longitude)
  }

  function createMeshLine(google, map, centerLatLng) {
    return new google.maps.Polyline({
      path: [centerLatLng, centerLatLng],
      geodesic: true,
      strokeColor: '#000000',
      strokeOpacity: 0.4,
      strokeWeight: 1.5,
      map,
      zIndex: 999,
    })
  }

  /**
   * Animate a marker from center to final position with a connecting line.
   * @param {Object} params - Animation parameters
   */
  function animateMeshMarker({ marker, line, centerLatLng, finalPosition, index }) {
    const google = globalThis.google
    const animationDuration = 400
    const delay = index * 30
    const startTime = Date.now()

    const startLat = centerLatLng.lat()
    const startLng = centerLatLng.lng()
    const endLat = finalPosition.lat()
    const endLng = finalPosition.lng()
    const latDiff = endLat - startLat
    const lngDiff = endLng - startLng

    setTimeout(() => {
      const animate = () => {
        const elapsed = Date.now() - startTime - delay
        if (elapsed < 0) {
          requestAnimationFrame(animate)
          return
        }

        const progress = Math.min(elapsed / animationDuration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)

        const currentLat = startLat + latDiff * eased
        const currentLng = startLng + lngDiff * eased
        const currentPosition = new google.maps.LatLng(currentLat, currentLng)

        marker.setPosition(currentPosition)
        line.setPath([centerLatLng, currentPosition])

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          line.setPath([centerLatLng, finalPosition])
        }
      }
      animate()
    }, delay)
  }

  /**
   * Create star mesh markers radiating from cluster center with connecting lines.
   * @param {google.maps.Map} map - Map instance
   * @param {google.maps.LatLng|Object} center - Cluster center position
   * @param {Array} stylists - Array of stylist objects in the cluster
   * @param {Function} onMarkerClick - Click handler for individual markers
   * @returns {Object} Object with markers, lines, center, and stylists arrays
   */
  function createStarMesh(map, center, stylists, onMarkerClick) {
    if (!map || !globalThis.google || !stylists || stylists.length === 0) return null

    const google = globalThis.google
    const { templates = {}, geometry = {} } = namespace
    const { buildMarkerIcon = () => null } = templates
    const { determineRoleCategory = () => 'default' } = geometry

    const centerLatLng = normalizeLatLng(center)
    if (!centerLatLng) return null

    const validStylists = stylists.filter((stylist) => isValidStylistLocation(stylist))
    if (validStylists.length === 0) return null

    const count = validStylists.length
    const radiusPixels = calculateMeshRadius(count)
    const positions = calculateStarMeshPositions(map, centerLatLng, count, radiusPixels)

    if (positions.length !== count) return null

    const markers = []
    const lines = []

    validStylists.forEach((stylist, index) => {
      const finalPosition = positions[index]
      if (!finalPosition) return

      const line = createMeshLine(google, map, centerLatLng)
      lines.push(line)

      const category = determineRoleCategory(stylist)
      const markerIcon = buildMarkerIcon(category, { active: false })

      const marker = new google.maps.Marker({
        position: centerLatLng,
        map,
        icon: markerIcon,
        title: stylist.name || '',
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + 100 + index,
        clickable: true,
        optimized: false,
      })

      marker._stylistEmail = stylist.email
      marker._isMeshMarker = true

      animateMeshMarker({ marker, line, centerLatLng, finalPosition, index })

      if (onMarkerClick) {
        google.maps.event.addListener(marker, 'click', (e) => {
          if (e && e.stop) e.stop()
          if (e && e.stopPropagation) e.stopPropagation()
          onMarkerClick(stylist, marker)
          return false
        })
      }

      markers.push(marker)
    })

    return { markers, lines, center: centerLatLng, stylists: validStylists }
  }

  /**
   * Remove all mesh markers and lines from the map.
   * @param {Object|Array} meshData - Object with markers and lines arrays, or just markers array
   */
  function removeStarMesh(meshData) {
    if (!meshData) return

    const markers = Array.isArray(meshData) ? meshData : meshData.markers || []
    const lines = Array.isArray(meshData) ? [] : meshData.lines || []

    markers.forEach((marker) => marker?.setMap?.(null))
    lines.forEach((line) => line?.setMap?.(null))
  }

  // ============================================================================
  // Exports
  // ============================================================================

  // Export to customCluster namespace (for backward compatibility with map.js)
  namespace.customCluster = Object.assign({}, namespace.customCluster, {
    calculateDistance,
    CLUSTER_DISTANCE_METERS,
  })

  // Export cluster renderer
  namespace.cluster = Object.assign({}, namespace.cluster, {
    CustomClusterRenderer,
  })

  // Export cluster mesh functions
  namespace.clusterMesh = Object.assign({}, namespace.clusterMesh, {
    calculateStarMeshPositions,
    createStarMesh,
    removeStarMesh,
  })
})()
